import {extendObservable, decorate, computed, action} from 'mobx';
import FiltersStore from 'components/meta-results-tabs/components/filters/store';
import * as R from 'ramda/src/index';
import Result from './result-model';
import {updateFilterOptions} from './components/filters/FiltersHelper';

const isBetween = (val1, val2) => R.both(R.gte(R.__, val1), R.lte(R.__, val2));
const filterResultsByProvider = (providerOptions) => R.pipe(
  R.map((val) => ({
    ...val,
    providers: R.filter((value) =>
      R.includes(value.provider.name, R.pluck('name', providerOptions)), val.providers)
  })),
  R.filter((val) => val.providers.length > 0)
);

// const filterResultsByTransportation = (values) => R.filter(
//   R.pipe(
//     R.prop('transportationTypes'),
//     R.all((val) => R.includes(val, values))
//   )
// );

const checkResultHasAirline = (values, combinations) => (data) => {
  const result = R.pipe(
    R.prop('airlines'),
    R.values,
    R.pluck('code'),
    R.uniq
  )(data);
  return combinations
    ? R.any((val) => R.includes(val, values))(result)
    : R.and(R.equals(1, R.length(result)), R.all((airline) => R.includes(airline, values))(result));
};

const filterResultsByAirline = (value, combinations) =>
  R.filter(
    checkResultHasAirline(value, combinations === null || combinations)
  );

const filterResultsByPrice = (price) =>
  R.filter(
    R.pipe(
      R.path(['minMetrics', 'price']),
      R.lte(R.__, price)
    )
  );

const filterResultsByStops = (value) =>
  R.filter(
    R.pipe(
      R.prop('legs'),
      R.pluck('stopsCount'),
      R.apply(Math.max),
      R.gte(value)
    )
  );

const filterResultsByTimes = (filtersStore) =>
  R.filter((result) => {
    const newLegs = result.legs.filter(({duration, segments}, index) => {
      if (result.legs.length > 2) {
        return true;
      }
      const type = index === 0 ? 'obTimes' : 'ibTimes';
      return (
        isBetween(
          filtersStore[type].duration.min,
          filtersStore[type].duration.value
        )(duration) &&
        R.and(
          isBetween(
            R.head(filtersStore[type].departure.value),
            R.last(filtersStore[type].departure.value)
          )(R.head(segments).route.departure.datetime.unix()),
          isBetween(
            R.head(filtersStore[type].arrival.value),
            R.last(filtersStore[type].arrival.value)
          )(R.last(segments).route.arrival.datetime.unix()),
        )
      );
    });
    return newLegs.length === result.legs.length;
  });

const createRoutes = ({dep, arr, isRoundtrip} ) => {
  const routes = [`${dep} - ${arr}`];
  if (isRoundtrip) {
    routes.push(`${arr} - ${dep}`);
  }
  return routes;
};

const getTotalFromPassengers = R.pipe(
  R.values,
  R.map(({count, price}) => count * price.total),
  R.sum
);

class Tab {
  constructor({resultsProvider, searchQuery, searchMaskStopLoading, resultsEventsChannel}) {
    const routes = createRoutes(searchQuery);
    extendObservable(this, {
      loadingResults: true,
      deactivateButtons: false,
      loader: true,
      results: [],
      filtersStore: new FiltersStore({routes, resultsEventsChannel})
    });

    Object.assign(this, {
      searchQuery,
      resultsEventsChannel
    });

    resultsProvider
      .subscribe({
        next: this.renderResults,
        complete: () => {
          this.loadingResults = false;
          this.loader = false;
          searchMaskStopLoading();
        }
      });
  }

  renderResults = (result) => {
    const cachedResults = [...this.results];
    result.forEach((item) => {
      const indexFound = R.findIndex(R.propEq('groupId', item.groupId))(cachedResults);
      if (indexFound < 0) {
        cachedResults.push(new Result(item));
      } else {
        const total = getTotalFromPassengers(item.passengers);
        const pr = {
          ...R.pick(['provider', 'passengers', 'id', 'resourceId'])(item),
          total
        };
        const providers = R.path([indexFound, 'providers'], cachedResults);
        const minPrice = R.path([indexFound, 'minMetrics', 'price'], cachedResults);
        providers.push(pr);
        cachedResults[indexFound].providers = R.sortBy(R.prop('total'))(providers);
        cachedResults[indexFound].minMetrics.price = Math.min(minPrice, total);
      }
    });
    this.results = [...cachedResults];

    this.filtersStore.setupFilters({
      ...updateFilterOptions({searchQuery: this.searchQuery, results: this.results})
    });

    this.loader = false;
  };

  get filteredResults() {
    const results = this.results;
    const filtersStore = this.filtersStore;
    const priceValue = filtersStore.price.value || filtersStore.price.max;
    const stopsValues = R.isNil(filtersStore.stops.value)
      ? filtersStore.stops.defaultValue
      : filtersStore.stops.value;
    const airlineOptions = R.filter(({code}) =>
      filtersStore.airlines.selectedOptions.indexOf(code) !== -1)(filtersStore.airlines.options);
    const providerOptions = R.filter(({name}) =>
      filtersStore.providers.selectedOptions.indexOf(name) !== -1)(filtersStore.providers.options);
    // const transportationOptions = R.filter(({name}) =>
    //   filtersStore.transportTypes.selectedOptions.indexOf(name) !== -1)(filtersStore.transportTypes.options);
    const airlinesWithCombinations = filtersStore.airlines.withCombinations;
    const metric = filtersStore.sorting.find((types) => types.selected).value;

    return R.pipe(
      filterResultsByPrice(priceValue),
      filterResultsByStops(stopsValues),
      filterResultsByAirline(R.pluck('code')(airlineOptions), airlinesWithCombinations),
      filterResultsByProvider(providerOptions),
      // filterResultsByTransportation(R.pluck('name')(transportationOptions)),
      filterResultsByTimes(filtersStore),
      R.sortWith([
        R.ascend(R.path(['minMetrics', metric])),
        R.ascend(R.path(['minMetrics', 'price']))
      ])
    )(results);
  }

  get resultsCounts() {
    return {
      remaining: this.filteredResults.length,
      total: this.results.length
    };
  }
}

decorate(Tab, {
  filteredResults: computed,
  resultsCounts: computed,
  renderResults: action
});

export default Tab;
